import React, { useRef, useState } from "react";

interface FormFieldProps {
  label: string;
  value: string;
  disabled?: boolean;
  placeholder?: string;
  onChange: (value: string) => void;
}

const FormField: React.FC<FormFieldProps> = ({
  label,
  value,
  disabled,
  placeholder,
  onChange,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isFocused, setIsFocused] = useState(false);

  const handleFocus = () => {
    setIsFocused(true);
    // Delay to ensure WebView handles focus properly
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        // Force cursor to end of input
        const length = inputRef.current.value.length;
        inputRef.current.setSelectionRange(length, length);
      }
    }, 100);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
    // Force re-render on iOS WebView
    if (inputRef.current) {
      inputRef.current.style.opacity = "0.99";
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.style.opacity = "1";
        }
      }, 10);
    }
  };

  return (
    <div className="flex flex-col mb-2 w-full">
      <label className="flex-1 shrink gap-2.5 px-2 w-full text-sm leading-loose text-gray-500 text-left">
        {label}
      </label>
      <input
        ref={inputRef}
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        className="flex mt-1 w-full text-base font-medium tracking-normal bg-white rounded-xl border border-solid border-slate-400 min-h-[56px] text-slate-400 flex-1 shrink gap-2 self-stretch px-3 py-2 min-w-[240px] size-full outline-none"
        style={{
          // iOS WebView specific fixes
          WebkitAppearance: "none",
          WebkitUserSelect: "text",
          userSelect: "text",
          transform: "translateZ(0)",
          WebkitTransform: "translateZ(0)",
          willChange: "contents",
          // Force re-render when focused to show cursor
          opacity: isFocused ? 0.99 : 1,
          // Ensure text color is visible
          color: value ? "#475569" : "#94a3b8", // slate-600 for text, slate-400 for placeholder
        }}
        disabled={disabled}
        // iOS specific attributes
        autoCapitalize="off"
        autoCorrect="off"
        spellCheck="false"
      />
    </div>
  );
};

export default FormField;
